/**
 * Base HTTP client for the admin UI.
 *
 * Handles:
 *  - Base URL configuration via Vite env (VITE_API_BASE_URL)
 *  - X-Tenant-Id header (Sprint 5: dev mode only; JWT lands in Sprint 7)
 *  - Typed responses
 *  - ApiError class with status + body
 *  - AbortController support for cancelling in-flight requests
 *
 * Sprint 6 will add: auth header from JWT, retry/backoff, request tracing.
 */

const DEFAULT_BASE_URL =
  (import.meta as unknown as { env: Record<string, string | undefined > }).env
    .VITE_API_BASE_URL ?? "http://localhost:5100";


export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}


/** Default demo tenant — kept exported for legacy callers / dev-token flow. */
export const DEMO_TENANT_ID = "decade00-0000-0000-0000-000000000001";


export interface RequestOptions {
  signal?: AbortSignal;
}


export interface MutationOptions<TBody> extends RequestOptions {
  body?: TBody;
}


/**
 * Hook invoked when any request comes back 401. Wired by `AppShell` to
 * trigger a refresh-then-retry or, if that fails, push the user to /login.
 * Kept as a module-level callback rather than React context so non-React
 * callers (interceptors, background tasks) can participate.
 */
type UnauthorizedHandler = () => Promise<string | null>;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}


/** Inline-imported to avoid a circular dependency with `shared/auth/store`. */
async function readBearerToken(): Promise<string | null> {
  const mod = await import("../auth/store");
  return mod.getAccessToken();
}


async function request<TResponse>(
  method: string,
  path: string,
  options: { body?: unknown } & RequestOptions = {},
): Promise<TResponse> {
  const init = await buildInit(method, options);

  let response: Response;
  try {
    response = await fetch(`${DEFAULT_BASE_URL}${path}`, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(
      `network error: ${(e as Error).message}`,
      0,
      null,
    );
  }

  // 401: give the unauthorized handler a chance to refresh and retry once.
  if (response.status === 401 && unauthorizedHandler) {
    const refreshed = await unauthorizedHandler();
    if (refreshed) {
      const retryInit = await buildInit(method, options, refreshed);
      response = await fetch(`${DEFAULT_BASE_URL}${path}`, retryInit);
    }
  }

  // 204 / 205 — no body
  if (response.status === 204 || response.status === 205) {
    return undefined as unknown as TResponse;
  }

  const ctype = response.headers.get("content-type") ?? "";
  const body: unknown = ctype.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const msg = extractErrorMessage(body) ?? `${response.status} ${response.statusText}`;
    throw new ApiError(msg, response.status, body);
  }

  return body as TResponse;
}


/** Build a fetch `RequestInit` from our request shape, attaching a Bearer.
 *
 * `overrideToken` is used by the 401-retry path so a freshly refreshed
 * token is applied without racing the in-flight Promise from
 * `readBearerToken()`.
 */
async function buildInit(
  method: string,
  options: { body?: unknown } & RequestOptions,
  overrideToken?: string,
): Promise<RequestInit> {
  const headers: Record<string, string> = {
    "Accept": "application/json",
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const token = overrideToken ?? (await readBearerToken());
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = { method, headers };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }
  return init;
}


function extractErrorMessage(body: unknown): string | null {
  if (typeof body === "string") return body;
  if (typeof body === "object" && body !== null) {
    const b = body as Record<string, unknown>;
    if (typeof b.detail === "string") return b.detail;
    if (typeof b.message === "string") return b.message;
  }
  return null;
}


export const api = {
  get:    <T>(path: string, opts?: RequestOptions)                    => request<T>("GET",    path, opts),
  post:   <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>("POST",   path, { ...opts, body }),
  put:    <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>("PUT",    path, { ...opts, body }),
  patch:  <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>("PATCH",  path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions)                    => request<T>("DELETE", path, opts),
};


export { DEFAULT_BASE_URL as API_BASE_URL };
